import React, { useState, useMemo, useEffect, useRef } from 'react';
import { ChevronRight, ChevronDown, ChevronUp, AlertTriangle, Copy, Check } from 'lucide-react';

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

const pathToKey = (path) => path.map(String).join('.');

const getType = (val) => {
    if (val === null) return 'null';
    if (Array.isArray(val)) return 'array';
    return typeof val;
};

const TYPE_COLOR = {
    string:  'text-green-400',
    number:  'text-orange-400',
    boolean: 'text-purple-400',
    object:  'text-blue-400',
    array:   'text-cyan-400',
    null:    'text-gray-500',
    integer: 'text-orange-400',
};

const TypeBadge = ({ type }) => (
    <span className={`font-mono text-xs flex-shrink-0 ${TYPE_COLOR[type] || 'text-gray-500'}`}>
        {type}
    </span>
);

const formatPrimitive = (val) => {
    if (val === null) return 'null';
    if (typeof val === 'string') {
        const s = val.length > 55 ? val.slice(0, 52) + '…' : val;
        return `"${s}"`;
    }
    return String(val);
};

const getUnionKeys = (src, tgt) => {
    const sKeys = (src && typeof src === 'object' && !Array.isArray(src)) ? Object.keys(src) : [];
    const tKeys = (tgt && typeof tgt === 'object' && !Array.isArray(tgt)) ? Object.keys(tgt) : [];
    const seen = new Set();
    const result = [];
    [...sKeys, ...tKeys].forEach(k => { if (!seen.has(k)) { seen.add(k); result.push(k); } });
    return result;
};

const META_KEYS = new Set(['detected_format']);

const filterMeta = (data) => {
    if (!data || typeof data !== 'object' || Array.isArray(data)) return data;
    const result = {};
    for (const [k, v] of Object.entries(data)) {
        if (!META_KEYS.has(k)) result[k] = v;
    }
    return result;
};

// ---------------------------------------------------------------------------
// Copy button
// ---------------------------------------------------------------------------

// Convert dot-notation pathKey to developer-friendly bracket notation
const formatPathForCopy = (pathKey) =>
    pathKey.split('.').reduce((acc, part) =>
        /^\d+$/.test(part) ? `${acc}[${part}]` : acc ? `${acc}.${part}` : part
    , '');

function CopyButton({ text, title = 'Copy path' }) {
    const [copied, setCopied] = useState(false);
    return (
        <button
            onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(text).then(() => {
                    setCopied(true);
                    setTimeout(() => setCopied(false), 1500);
                });
            }}
            className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded text-gray-600 hover:text-gray-300 flex-shrink-0"
            title={title}
        >
            {copied ? <Check size={10} className="text-green-400" /> : <Copy size={10} />}
        </button>
    );
}

// ---------------------------------------------------------------------------
// Search highlight
// ---------------------------------------------------------------------------

function HighlightText({ text, query }) {
    if (!query || !text) return <>{text}</>;
    const str = String(text);
    const idx = str.toLowerCase().indexOf(query.toLowerCase());
    if (idx === -1) return <>{str}</>;
    return (
        <>
            {str.slice(0, idx)}
            <mark className="bg-yellow-400/40 text-inherit rounded-sm not-italic">{str.slice(idx, idx + query.length)}</mark>
            {str.slice(idx + query.length)}
        </>
    );
}

// ---------------------------------------------------------------------------
// Array LCS alignment
// ---------------------------------------------------------------------------

// Similarity score between two array items (0 = no match, 1 = identical)
function itemSimilarity(a, b) {
    if (a === b) return 1;
    const ta = getType(a), tb = getType(b);
    if (ta !== tb) return 0;
    if (ta === 'object') {
        const aKeys = Object.keys(a || {});
        const bKeys = Object.keys(b || {});
        const allKeys = new Set([...aKeys, ...bKeys]);
        if (!allKeys.size) return 1;
        let matches = 0;
        allKeys.forEach(k => {
            if (JSON.stringify((a)[k]) === JSON.stringify((b)[k])) matches++;
        });
        return matches / allKeys.size;
    }
    if (ta === 'array') {
        if (!a.length && !b.length) return 1;
        if (!a.length || !b.length) return 0;
        return Math.min(a.length, b.length) / Math.max(a.length, b.length) * 0.5;
    }
    // Different primitives of same type — partial credit for strings
    if (ta === 'string') {
        const shorter = Math.min(a.length, b.length);
        const longer  = Math.max(a.length, b.length);
        if (!longer) return 1;
        let common = 0;
        for (let i = 0; i < shorter; i++) if (a[i] === b[i]) common++;
        return common / longer * 0.4; // cap at 0.4 so we only match if quite similar
    }
    return 0;
}

// Match threshold — pairs below this are treated as unrelated (added + removed)
const MATCH_THRESHOLD = 0.4;

// Returns aligned pairs: { srcIdx: number|null, tgtIdx: number|null }[]
function computeArrayAlignment(srcArr, tgtArr) {
    const n = srcArr.length, m = tgtArr.length;
    if (!n && !m) return [];
    if (!n) return tgtArr.map((_, j) => ({ srcIdx: null, tgtIdx: j }));
    if (!m) return srcArr.map((_, i) => ({ srcIdx: i, tgtIdx: null }));

    // Build similarity matrix
    const sim = Array.from({ length: n }, (_, i) =>
        Array.from({ length: m }, (_, j) => itemSimilarity(srcArr[i], tgtArr[j]))
    );

    // DP table: dp[i][j] = best score for aligning srcArr[i..] with tgtArr[j..]
    const dp = Array.from({ length: n + 1 }, () => new Float32Array(m + 1));
    for (let i = n - 1; i >= 0; i--) {
        for (let j = m - 1; j >= 0; j--) {
            const matchScore = sim[i][j] >= MATCH_THRESHOLD ? dp[i + 1][j + 1] + sim[i][j] : -Infinity;
            dp[i][j] = Math.max(matchScore, dp[i + 1][j], dp[i][j + 1]);
        }
    }

    // Traceback
    const result = [];
    let i = 0, j = 0;
    while (i < n || j < m) {
        if (i >= n) {
            result.push({ srcIdx: null, tgtIdx: j++ });
        } else if (j >= m) {
            result.push({ srcIdx: i++, tgtIdx: null });
        } else {
            const canMatch = sim[i][j] >= MATCH_THRESHOLD;
            const matchVal = canMatch ? dp[i + 1][j + 1] + sim[i][j] : -Infinity;
            if (canMatch && matchVal >= dp[i + 1][j] && matchVal >= dp[i][j + 1]) {
                result.push({ srcIdx: i++, tgtIdx: j++ });
            } else if (dp[i + 1][j] >= dp[i][j + 1]) {
                result.push({ srcIdx: i++, tgtIdx: null });
            } else {
                result.push({ srcIdx: null, tgtIdx: j++ });
            }
        }
    }
    return result;
}

// ---------------------------------------------------------------------------
// Collect paths open by default (depth < 2)
// ---------------------------------------------------------------------------

function collectDepth2Paths(srcData, tgtData) {
    const paths = new Set();
    const walk = (src, tgt, path, depth) => {
        if (depth >= 2) return;
        if (path.length > 0) paths.add(pathToKey(path));
        const srcObj = src && typeof src === 'object' && !Array.isArray(src) ? src : {};
        const tgtObj = tgt && typeof tgt === 'object' && !Array.isArray(tgt) ? tgt : {};
        const srcArr = Array.isArray(src) ? src : null;
        const tgtArr = Array.isArray(tgt) ? tgt : null;
        if (srcArr || tgtArr) {
            const a = srcArr || [];
            const b = tgtArr || [];
            Array.from({ length: Math.max(a.length, b.length) }, (_, i) =>
                walk(a[i], b[i], [...path, i], depth + 1));
            return;
        }
        if (!Object.keys(srcObj).length && !Object.keys(tgtObj).length) return;
        getUnionKeys(srcObj, tgtObj).forEach(k => walk(srcObj[k], tgtObj[k], [...path, k], depth + 1));
    };
    const srcObj = srcData && typeof srcData === 'object' && !Array.isArray(srcData) ? srcData : {};
    const tgtObj = tgtData && typeof tgtData === 'object' && !Array.isArray(tgtData) ? tgtData : {};
    getUnionKeys(srcObj, tgtObj).forEach(k => walk(srcObj[k], tgtObj[k], [k], 0));
    return paths;
}

// ---------------------------------------------------------------------------
// buildRows — flat RowDescriptor list
// ---------------------------------------------------------------------------

function buildRowsForKey(srcKey, tgtKey, srcVal, tgtVal, path, depth, maps, expandedPaths, opts) {
    const { parentStatus = null, isViewOnly = false } = opts;
    const rows = [];
    const pathKey = pathToKey(path);

    const isAdded   = srcVal === undefined && tgtVal !== undefined;
    const isRemoved = srcVal !== undefined && tgtVal === undefined;
    const changeInfo   = maps.changed.get(pathKey) || null;
    const typeInfo     = maps.typeChangeMap.get(pathKey) || null;
    const isRenamed    = !!(maps.renamesByOldField.get(srcKey) || (srcKey !== tgtKey));

    const effectiveIsAdded   = isAdded   && !isRenamed;
    const effectiveIsRemoved = isRemoved && !isRenamed;

    let status;
    if (parentStatus) {
        status = parentStatus;
    } else if (isRenamed && !isViewOnly) {
        status = 'renamed';
    } else if (effectiveIsAdded) {
        status = 'added';
    } else if (effectiveIsRemoved) {
        status = 'removed';
    } else if (typeInfo) {
        status = 'type-changed';
    } else if (changeInfo) {
        status = 'changed';
    } else {
        status = 'unchanged';
    }

    const displayVal  = tgtVal !== undefined ? tgtVal : srcVal;
    const displayType = getType(displayVal);
    const isExpandable = displayType === 'object' || displayType === 'array';
    const isOpen = expandedPaths === 'all'
        ? true
        : (expandedPaths instanceof Set ? expandedPaths.has(pathKey) : depth < 2);
    const hasDescendantChanges = !['added','removed','changed','type-changed','renamed'].includes(status) &&
        isExpandable && maps.ancestorPaths.has(pathKey);

    rows.push({ pathKey, depth, srcKey, tgtKey, srcVal, tgtVal, status, parentStatus, changeInfo, typeInfo, isExpandable, isOpen, hasDescendantChanges, renameScore: isRenamed ? maps.renameDetails?.get(srcKey)?.similarity : undefined });

    if (isExpandable && isOpen) {
        const childParentStatus =
            parentStatus === 'added'   ? 'added'   :
            parentStatus === 'removed' ? 'removed' :
            status === 'added'         ? 'added'   :
            status === 'removed'       ? 'removed' : null;

        rows.push(...buildRows(srcVal, tgtVal, maps, expandedPaths, depth + 1, path, { parentStatus: childParentStatus, isViewOnly }));
    }

    return rows;
}

function buildRows(srcData, tgtData, maps, expandedPaths, depth, path, opts) {
    const { parentStatus = null, isViewOnly = false } = opts;
    const rows = [];

    const srcIsArr = Array.isArray(srcData);
    const tgtIsArr = Array.isArray(tgtData);

    if (srcIsArr || tgtIsArr) {
        const srcArr = srcIsArr ? srcData : [];
        const tgtArr = tgtIsArr ? tgtData : [];
        // LCS-based alignment so reordered/inserted items don't cascade as false changes
        const alignment = isViewOnly
            ? srcArr.map((_, i) => ({ srcIdx: i, tgtIdx: i }))
            : computeArrayAlignment(srcArr, tgtArr);
        let srcCursor = 0, tgtCursor = 0;
        alignment.forEach(({ srcIdx, tgtIdx }) => {
            const sv = srcIdx !== null ? srcArr[srcIdx] : undefined;
            const tv = tgtIdx !== null ? tgtArr[tgtIdx] : undefined;
            // Use a stable display index: prefer srcIdx, fall back to tgtIdx
            const displayIdx = srcIdx ?? tgtIdx;
            rows.push(...buildRowsForKey(
                String(displayIdx), String(displayIdx),
                sv, tv,
                [...path, displayIdx], depth, maps, expandedPaths, opts
            ));
        });
        return rows;
    }

    const srcObj = srcData && typeof srcData === 'object' ? srcData : {};
    const tgtObj = tgtData && typeof tgtData === 'object' ? tgtData : {};

    const keys = isViewOnly
        ? Object.keys(srcObj)
        : getUnionKeys(srcObj, tgtObj).filter(k => !(maps.renamesByNewField.has(k) && !maps.renamesByOldField.has(k)));

    for (const k of keys) {
        const renamedTo = !isViewOnly ? maps.renamesByOldField.get(k) : undefined;
        const tgtKey = renamedTo || k;
        const srcVal = srcObj[k];
        const tgtVal = isViewOnly ? srcObj[k] : (renamedTo ? tgtObj[renamedTo] : tgtObj[k]);
        rows.push(...buildRowsForKey(k, tgtKey, srcVal, tgtVal, [...path, k], depth, maps, expandedPaths, opts));
    }

    return rows;
}

// ---------------------------------------------------------------------------
// CellContent — renders one side of a split row
// ---------------------------------------------------------------------------

function CellContent({ row, side, onToggle, isViewOnly, changesFilter, searchQuery, searchType, constraints = [], showTypes = true }) {
    const { status, depth, srcKey, tgtKey, srcVal, tgtVal, parentStatus, changeInfo, typeInfo, isExpandable, isOpen, hasDescendantChanges, pathKey, renameScore } = row;

    const isSrc = side === 'src';
    const val = isSrc ? srcVal : tgtVal;
    const nodeKey = isSrc ? srcKey : tgtKey;

    // Blank placeholder
    if (!isViewOnly && status === 'added'   && isSrc)  return <div className="flex-1 min-w-0" />;
    if (!isViewOnly && status === 'removed' && !isSrc) return <div className="flex-1 min-w-0" />;

    // Indicator symbol
    const indicatorEl = (() => {
        if (status === 'added' && !isSrc)         return <span className="text-green-500 font-bold text-xs w-5 text-center flex-shrink-0 select-none">+</span>;
        if (status === 'removed' && isSrc)        return <span className="text-red-500 font-bold text-xs w-5 text-center flex-shrink-0 select-none">−</span>;
        if ((status === 'changed' || status === 'type-changed') && isSrc) return <span className="text-yellow-500 font-bold text-xs w-5 text-center flex-shrink-0 select-none">~</span>;
        if (status === 'renamed' && isSrc)        return <span className="text-orange-400 font-bold text-xs w-5 text-center flex-shrink-0 select-none">↔</span>;
        return <span className="w-5 flex-shrink-0 select-none" />;
    })();

    const keyColorClass =
        status === 'added'   ? 'text-green-300'  :
        status === 'removed' ? 'text-red-300'    :
        status === 'renamed' ? 'text-orange-300' :
        'text-[#9cdcfe]';

    const valType = getType(val);
    const isExpandableVal = valType === 'object' || valType === 'array';

    const typeBadgeType = typeInfo
        ? (isSrc ? typeInfo.old_type : typeInfo.new_type)
        : getType(val !== undefined ? val : (isSrc ? tgtVal : srcVal));

    const isBreaking = (status === 'removed' && !parentStatus) || typeInfo?.severity === 'high';

    // Whether to highlight keys / values based on search type
    const q = searchQuery?.trim() || '';
    const highlightKeys = q && (searchType === 'all' || searchType === 'segment');
    const highlightVals = q && (searchType === 'all' || searchType === 'value');

    const renderValue = () => {
        if (isExpandableVal) {
            return (
                <span className="text-gray-600 text-xs ml-1 flex-shrink-0">
                    {valType === 'array' ? `[${val?.length ?? 0}]` : `{${Object.keys(val || {}).length}}`}
                </span>
            );
        }
        if (changeInfo && !parentStatus) {
            const v = isSrc ? changeInfo.old_value : changeInfo.new_value;
            const cls = isSrc ? 'text-red-400/80 line-through' : 'text-green-400/80';
            return (
                <span className={`font-mono ml-1.5 truncate max-w-[130px] ${cls}`}>
                    {highlightVals ? <HighlightText text={formatPrimitive(v)} query={q} /> : formatPrimitive(v)}
                </span>
            );
        }
        if (val !== undefined) {
            const colors = { string: 'text-green-400', number: 'text-orange-400', boolean: 'text-purple-400', null: 'text-gray-500' };
            return (
                <span className={`font-mono ml-1.5 truncate max-w-[130px] ${colors[valType] || 'text-gray-400/70'}`}>
                    {highlightVals ? <HighlightText text={formatPrimitive(val)} query={q} /> : formatPrimitive(val)}
                </span>
            );
        }
        return null;
    };

    return (
        <div
            className="flex items-center flex-1 min-w-0 py-0.5 pr-2 font-mono text-xs"
            style={{ paddingLeft: `${8 + depth * 18}px` }}
        >
            {indicatorEl}
            {isExpandable ? (
                <button onClick={onToggle} className="mr-1 text-gray-600 hover:text-gray-300 flex-shrink-0 p-0.5 font-mono">
                    {isOpen ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
                </button>
            ) : (
                <span className="w-[19px] flex-shrink-0 mr-1" />
            )}

            <span className={`text-xs font-mono flex-shrink-0 ${keyColorClass}`}>
                {highlightKeys ? <HighlightText text={nodeKey} query={q} /> : nodeKey}
            </span>
            <span className="text-gray-600 text-xs mx-1 flex-shrink-0">:</span>
            {renderValue()}
            <span className="flex-1 min-w-0" />

            {/* Copy field path — use in mapping rules, transformation code, or to reference this field exactly */}
            {isSrc && <CopyButton text={formatPathForCopy(pathKey)} title={`Copy field path — use in mapping rules or code\n${formatPathForCopy(pathKey)}`} />}

            {/* Breaking icon on src side only */}
            {isBreaking && isSrc && <AlertTriangle size={11} className="text-red-500 mr-1.5 flex-shrink-0" />}
            {showTypes && <TypeBadge type={typeBadgeType} />}

            {/* Rename confidence score */}
            {status === 'renamed' && isSrc && renameScore !== undefined && (
                <span className="ml-1.5 text-[10px] text-orange-400/50 flex-shrink-0 tabular-nums">
                    {Math.round(renameScore * 100)}%
                </span>
            )}

            {/* Inline constraint diffs */}
            {isSrc && constraints?.length > 0 && (
                <span className="ml-1.5 flex items-center gap-0.5 flex-shrink-0">
                    {constraints.slice(0, 4).map((c, i) => (
                        <span
                            key={i}
                            className={`text-[9px] px-1 py-0.5 rounded font-mono ${c.action === 'added' ? 'bg-green-900/30 text-green-400' : 'bg-red-900/30 text-red-400'}`}
                            title={`${c.action === 'added' ? 'Added' : 'Removed'} constraint: ${c.name}${c.value !== undefined ? ' = ' + JSON.stringify(c.value) : ''}`}
                        >
                            {c.action === 'added' ? '+' : '−'}{c.name}
                        </span>
                    ))}
                    {constraints.length > 4 && <span className="text-[9px] text-gray-600">+{constraints.length - 4}</span>}
                </span>
            )}

            {/* "contains changes" hint on src side of collapsed nodes */}
            {hasDescendantChanges && !isOpen && changesFilter === 'all' && isSrc && (
                <span className="ml-2 text-[10px] text-yellow-600/70 flex-shrink-0 italic">contains changes</span>
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const FORMAT_LABEL = {
    openapi: 'OpenAPI', json_schema: 'JSON Schema', avro: 'Avro Schema',
    yaml: 'YAML', xml: 'XML', csv: 'CSV', json: 'JSON',
};

const SCHEMA_NOISE = new Set(['schema_constraint', 'schema_set_item']);

// Background for each cell in split view
function cellBg(status, side) {
    if (status === 'added')        return side === 'src' ? '' : 'bg-green-900/10';
    if (status === 'removed')      return side === 'src' ? 'bg-red-900/10' : '';
    if (status === 'changed')      return 'bg-yellow-900/10';
    if (status === 'type-changed') return 'bg-yellow-900/10';
    if (status === 'renamed')      return 'bg-orange-900/10';
    return '';
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const JsonSchemaTreeViewer = ({
    source,
    target,
    diffResult,
    suggestedRenames = [],
    typeChanges = [],
    sourceName = 'Source',
    targetName = 'Target',
    searchQuery = '',
    searchType = 'all',
    changesFilter = 'all',
    currentMatchIndex = 0,
    onSearchMatchesChange,
    showTypes = true,
}) => {
    const [expandedPaths, setExpandedPaths] = useState(null); // null = use initialPaths
    const [allExpanded, setAllExpanded] = useState(false);
    const containerRef = useRef(null);

    const srcData = useMemo(() => filterMeta(source), [source]);
    const tgtData = useMemo(() => filterMeta(target), [target]);
    const isViewOnly = !target;

    const maps = useMemo(() => {
        const added         = new Set();
        const removed       = new Set();
        const changed       = new Map();
        const typeChangeMap = new Map();
        const renamesByOldField = new Map();
        const renamesByNewField = new Map();

        (diffResult?.added   || []).forEach(item => { if (Array.isArray(item.path)) added.add(pathToKey(item.path)); });
        (diffResult?.removed || []).forEach(item => { if (Array.isArray(item.path)) removed.add(pathToKey(item.path)); });
        (diffResult?.changed || []).forEach(item => { if (Array.isArray(item.path)) changed.set(pathToKey(item.path), item); });
        (typeChanges         || []).forEach(tc  => { if (Array.isArray(tc.path))   typeChangeMap.set(pathToKey(tc.path), tc); });
        (suggestedRenames    || []).forEach(r   => {
            if (r.old_field) renamesByOldField.set(r.old_field, r.new_field);
            if (r.new_field) renamesByNewField.set(r.new_field, r.old_field);
        });

        const addAncestorsTo = (pk, set) => {
            const parts = pk.split('.');
            for (let i = 1; i < parts.length; i++) set.add(parts.slice(0, i).join('.'));
        };

        const ancestorPaths         = new Set();
        const ancestorPathsAdded    = new Set();
        const ancestorPathsRemoved  = new Set();
        const ancestorPathsChanged  = new Set();
        const ancestorPathsBreaking = new Set();

        [...changed.keys(), ...added, ...removed, ...typeChangeMap.keys()].forEach(pk => addAncestorsTo(pk, ancestorPaths));
        [...added].forEach(pk => addAncestorsTo(pk, ancestorPathsAdded));
        [...removed].forEach(pk => addAncestorsTo(pk, ancestorPathsRemoved));
        [...changed.keys(), ...typeChangeMap.keys()].forEach(pk => addAncestorsTo(pk, ancestorPathsChanged));
        [...removed].forEach(pk => addAncestorsTo(pk, ancestorPathsBreaking));
        [...typeChangeMap.entries()].filter(([, tc]) => tc.severity === 'high').forEach(([pk]) => addAncestorsTo(pk, ancestorPathsBreaking));

        const renameDetails = new Map(); // oldField → full rename object (has .similarity)
        (suggestedRenames || []).forEach(r => { if (r.old_field) renameDetails.set(r.old_field, r); });

        return { added, removed, changed, typeChangeMap, renamesByOldField, renamesByNewField, ancestorPaths, ancestorPathsAdded, ancestorPathsRemoved, ancestorPathsChanged, ancestorPathsBreaking, renameDetails };
    }, [diffResult, typeChanges, suggestedRenames]);

    // Build inline constraint diffs grouped by parent path
    const constraintsByParent = useMemo(() => {
        const map = new Map();
        const add = (item, action) => {
            if (!Array.isArray(item.path) || item.path.length === 0) return;
            if (item.semantic_type !== 'schema_constraint' && item.semantic_type !== 'schema_set_item') return;
            const parentKey = pathToKey(item.path.slice(0, -1));
            if (!map.has(parentKey)) map.set(parentKey, []);
            map.get(parentKey).push({ action, name: item.path[item.path.length - 1], value: item.value, type: item.semantic_type });
        };
        (diffResult?.added   || []).forEach(i => add(i, 'added'));
        (diffResult?.removed || []).forEach(i => add(i, 'removed'));
        return map;
    }, [diffResult]);

    // Default: depth < 2 paths are open
    const initialPaths = useMemo(() => collectDepth2Paths(srcData, tgtData), [srcData, tgtData]);

    // When filter is active force-expand everything so matches are visible
    const effectiveExpandedPaths = changesFilter !== 'all' ? 'all' : (expandedPaths ?? initialPaths);

    const allRows = useMemo(() =>
        buildRows(srcData, tgtData, maps, effectiveExpandedPaths, 0, [], { isViewOnly }),
        [srcData, tgtData, maps, effectiveExpandedPaths, isViewOnly]
    );

    // Filter to visible rows
    const visibleRows = useMemo(() => {
        if (changesFilter === 'all') return allRows;
        return allRows.filter(row => {
            const { status, pathKey, typeInfo } = row;
            if (changesFilter === 'added')    return status === 'added';
            if (changesFilter === 'removed')  return status === 'removed';
            if (changesFilter === 'changed')  return status === 'changed' || status === 'type-changed';
            if (changesFilter === 'renamed')  return status === 'renamed';
            if (changesFilter === 'breaking') return status === 'removed' || (status === 'type-changed' && typeInfo?.severity === 'high');
            return true;
        });
    }, [allRows, changesFilter]);

    const handleToggle = (pathKey) => {
        setExpandedPaths(prev => {
            const base = new Set(prev ?? initialPaths);
            if (base.has(pathKey)) base.delete(pathKey);
            else base.add(pathKey);
            return base;
        });
    };

    const handleToggleAll = () => {
        if (allExpanded) {
            setExpandedPaths(new Set());
            setAllExpanded(false);
        } else {
            const all = new Set();
            const collect = (src, tgt, path) => {
                const srcO = src && typeof src === 'object' && !Array.isArray(src) ? src : {};
                const tgtO = tgt && typeof tgt === 'object' && !Array.isArray(tgt) ? tgt : {};
                const srcA = Array.isArray(src) ? src : null;
                const tgtA = Array.isArray(tgt) ? tgt : null;
                if (path.length > 0) all.add(pathToKey(path));
                if (srcA || tgtA) {
                    const a = srcA || [], b = tgtA || [];
                    Array.from({ length: Math.max(a.length, b.length) }, (_, i) => collect(a[i], b[i], [...path, i]));
                } else {
                    getUnionKeys(srcO, tgtO).forEach(k => collect(srcO[k], tgtO[k], [...path, k]));
                }
            };
            collect(srcData, tgtData, []);
            setExpandedPaths(all);
            setAllExpanded(true);
        }
    };

    // Search filtering + match reporting
    const { displayRows, matchingPathsList } = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        if (!q) return { displayRows: visibleRows, matchingPathsList: [] };

        const matchingPaths = new Set();
        const orderedMatches = [];
        visibleRows.forEach(row => {
            const keyMatch = (searchType === 'all' || searchType === 'segment') &&
                (row.srcKey?.toLowerCase().includes(q) || row.tgtKey?.toLowerCase().includes(q));
            const valMatch = (searchType === 'all' || searchType === 'value') && (() => {
                const check = (v) => v !== null && v !== undefined && typeof v !== 'object' && String(v).toLowerCase().includes(q);
                return check(row.srcVal) || check(row.tgtVal) ||
                    (row.changeInfo && (String(row.changeInfo.old_value).toLowerCase().includes(q) || String(row.changeInfo.new_value).toLowerCase().includes(q)));
            })();
            if (keyMatch || valMatch) {
                matchingPaths.add(row.pathKey);
                orderedMatches.push(row.pathKey);
            }
        });

        const ancestorPaths = new Set();
        matchingPaths.forEach(pk => {
            const parts = pk.split('.');
            for (let i = 1; i < parts.length; i++) ancestorPaths.add(parts.slice(0, i).join('.'));
        });

        return {
            displayRows: visibleRows.filter(row => matchingPaths.has(row.pathKey) || ancestorPaths.has(row.pathKey)),
            matchingPathsList: orderedMatches,
        };
    }, [visibleRows, searchQuery, searchType]);

    // Report match count to parent (SmartSearchBar)
    useEffect(() => {
        onSearchMatchesChange?.(matchingPathsList.map((_, i) => i));
    }, [matchingPathsList, onSearchMatchesChange]);

    // Scroll to current match when index changes
    useEffect(() => {
        if (!currentMatchIndex || !matchingPathsList.length) return;
        const pk = matchingPathsList[currentMatchIndex - 1];
        if (!pk) return;
        const el = containerRef.current?.querySelector(`[data-nodeid="${pk}"]`);
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            el.style.outline = '1px solid rgb(96 165 250)';
            el.style.outlineOffset = '-1px';
            setTimeout(() => { el.style.outline = ''; el.style.outlineOffset = ''; }, 2000);
        }
    }, [currentMatchIndex, matchingPathsList]);

    // Summary counts
    const renameOldFields = useMemo(() => new Set((suggestedRenames || []).map(r => r.old_field)), [suggestedRenames]);
    const renameNewFields = useMemo(() => new Set((suggestedRenames || []).map(r => r.new_field)), [suggestedRenames]);
    const renamedCount    = suggestedRenames?.length || 0;
    const addedCount      = (diffResult?.added   || []).filter(item =>
        !renameNewFields.has(item.path?.[item.path.length - 1]) && !SCHEMA_NOISE.has(item.semantic_type)).length;
    const removedCount    = (diffResult?.removed || []).filter(item =>
        !renameOldFields.has(item.path?.[item.path.length - 1]) && !SCHEMA_NOISE.has(item.semantic_type)).length;
    const constraintCount = [...(diffResult?.added || []), ...(diffResult?.removed || [])].filter(
        item => SCHEMA_NOISE.has(item.semantic_type)).length;
    const changedCount    = diffResult?.changed?.length || 0;
    const detectedFormat = source?.detected_format || target?.detected_format;
    const formatLabel    = FORMAT_LABEL[detectedFormat] || 'JSON';
    const openApiRequiredBreaking = detectedFormat === 'openapi'
        ? (diffResult?.added || []).filter(i => i.semantic_type === 'schema_set_item' && Array.isArray(i.path) && i.path.includes('required')).length
        : 0;
    const breakingCount   = (typeChanges || []).filter(tc => tc.severity === 'high').length + removedCount + openApiRequiredBreaking;

    return (
        <div className="flex flex-col h-full bg-[#1e1e1e] overflow-hidden">
            {/* Toolbar */}
            <div className="flex items-center justify-between px-3 py-1 bg-[#2d2d2d] border-b border-[#3c3c3c] flex-shrink-0 gap-2">
                <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-[10px] px-1.5 py-0.5 bg-blue-900/40 border border-blue-700/30 rounded text-blue-300 font-mono flex-shrink-0">
                        {formatLabel}
                    </span>
                    {!isViewOnly && (
                        <span className="text-xs text-gray-500 flex-shrink-0">
                            {sourceName} / {targetName}
                        </span>
                    )}
                    <button
                        onClick={handleToggleAll}
                        className="flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded bg-blue-900/30 text-blue-300 hover:bg-blue-900/50 transition-colors flex-shrink-0"
                    >
                        {allExpanded ? <><ChevronUp size={12} /> Collapse</> : <><ChevronDown size={12} /> Expand</>}
                    </button>
                </div>

                {!isViewOnly && (
                    <div className="flex items-center gap-3 text-xs flex-shrink-0">
                        {addedCount > 0 && <span className="text-green-400 font-medium">+{addedCount} added</span>}
                        {removedCount > 0 && <span className="text-red-400 font-medium">−{removedCount} removed</span>}
                        {changedCount > 0 && <span className="text-yellow-400 font-medium">~{changedCount} changed</span>}
                        {renamedCount > 0 && <span className="text-orange-400 font-medium">↔{renamedCount} renamed</span>}
                        {constraintCount > 0 && <span className="text-gray-400 font-medium">⊕{constraintCount} constraints</span>}
                        {breakingCount > 0 && (
                            <span className={`flex items-center gap-1 font-medium ${changesFilter === 'breaking' ? 'text-red-300' : 'text-red-500'}`}>
                                <AlertTriangle size={10} />{breakingCount} breaking
                            </span>
                        )}
                        {addedCount + removedCount + changedCount + renamedCount + constraintCount === 0 && (
                            <span className="text-gray-500 text-[11px]">No structural changes detected</span>
                        )}
                    </div>
                )}
            </div>

            {/* Column headers (compare mode only) */}
            {!isViewOnly && (
                <div className="flex flex-shrink-0 border-b border-[#3c3c3c] bg-[#252526]">
                    <div className="w-10 flex-shrink-0 bg-gray-800 border-r border-[#3c3c3c]" />
                    <div className="flex-1 px-3 py-1 text-xs font-semibold text-blue-300 truncate border-r border-[#3c3c3c]">
                        {sourceName}
                    </div>
                    <div className="flex-1 px-3 py-1 text-xs font-semibold text-green-300 truncate">
                        {targetName}
                    </div>
                </div>
            )}

            {/* OpenAPI breaking change note */}
            {detectedFormat === 'openapi' && !isViewOnly && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-950/40 border-b border-blue-800/30 flex-shrink-0">
                    <AlertTriangle size={11} className="text-blue-400 flex-shrink-0" />
                    <span className="text-[11px] text-blue-300/80">
                        OpenAPI: adding fields to <code className="font-mono bg-blue-900/30 px-0.5 rounded">required</code> in request schemas is a breaking change for API consumers. Removing from <code className="font-mono bg-blue-900/30 px-0.5 rounded">required</code> is not.
                    </span>
                </div>
            )}

            {/* Rows */}
            <div ref={containerRef} className="flex-1 overflow-auto font-mono text-xs">
                {displayRows.length === 0 ? (
                    <div className="flex items-center justify-center h-32 text-gray-500 text-sm font-sans">
                        {searchQuery ? 'No matches found' : 'No data to display'}
                    </div>
                ) : displayRows.map((row, idx) => {
                    const isChanged = row.status !== 'unchanged';
                    return (
                        <div
                            key={row.pathKey + idx}
                            className="group flex items-stretch border-b border-[#2a2a2a] hover:bg-white/[0.02] cursor-default"
                            data-nodeid={row.pathKey}
                            data-change={isChanged ? 'true' : undefined}
                        >
                            {/* Line number gutter */}
                            <span className="w-10 text-right text-gray-400 text-xs select-none pr-2 bg-gray-800 border-r border-[#3c3c3c] flex-shrink-0 flex items-center justify-end">
                                {idx + 1}
                            </span>

                            {isViewOnly ? (
                                <CellContent row={row} side="src" onToggle={() => handleToggle(row.pathKey)} isViewOnly changesFilter={changesFilter} searchQuery={searchQuery} searchType={searchType} showTypes={showTypes} />
                            ) : (
                                <>
                                    <div className={`flex-1 flex min-w-0 ${cellBg(row.status, 'src')}`}>
                                        <CellContent row={row} side="src" onToggle={() => handleToggle(row.pathKey)} isViewOnly={false} changesFilter={changesFilter} searchQuery={searchQuery} searchType={searchType} constraints={constraintsByParent.get(row.pathKey)} showTypes={showTypes} />
                                    </div>
                                    <div className="w-px bg-[#3c3c3c] flex-shrink-0" />
                                    <div className={`flex-1 flex min-w-0 ${cellBg(row.status, 'tgt')}`}>
                                        <CellContent row={row} side="tgt" onToggle={() => handleToggle(row.pathKey)} isViewOnly={false} changesFilter={changesFilter} searchQuery={searchQuery} searchType={searchType} showTypes={showTypes} />
                                    </div>
                                </>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default JsonSchemaTreeViewer;
